import { type FieldMetadata, useInputControl } from '@conform-to/react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Box, Flex, Text } from '@radix-ui/themes'
import { type TFunction } from 'i18next'
import * as React from 'react'
import { RxStarFilled, RxStar } from 'react-icons/rx'

import { FormFieldError } from '#app/components/ui/form-field-error.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label/label.tsx'

/* -------------------------------------------------------------------------------------------------
 * RatingInput
 * -----------------------------------------------------------------------------------------------*/

type RatingInputElement = React.ElementRef<typeof RadioGroupPrimitive.Root>
type RatingInputProps = {
	label: string
	meta: FieldMetadata<string>
	t: TFunction<string, undefined>
}

const RatingInput = React.forwardRef<RatingInputElement, RatingInputProps>(
	(props, forwardedRef) => {
		const { label, meta, t } = props

		const [hover, setHover] = React.useState<string | undefined>(undefined)
		const control = useInputControl(meta)

		return (
			<Flex direction="column" gapY="2" className="app-Rating">
				<Text as="p" color="gray">
					{label}
				</Text>
				<Box ml="1">
					<Flex direction="column" gapY="2">
						<Flex asChild gapX="2">
							<RadioGroupPrimitive.Root
								ref={forwardedRef}
								orientation="horizontal"
								name={meta.name}
								defaultValue={meta.initialValue}
								value={control.value}
								onValueChange={control.change}
								onBlur={control.blur}
							>
								{[...Array(5).keys()].map((item) => {
									const x = (item + 1).toString()
									return (
										<Flex
											asChild
											key={x}
											direction="column"
											align="center"
											justify="start"
											gapY="3"
										>
											<RadioGroupPrimitive.Item
												value={x}
												id={`${meta.id}-${x}`}
												onMouseOver={() => setHover(x)}
												onMouseLeave={() => setHover(undefined)}
											>
												<RadioGroupPrimitive.Indicator asChild />
												{(typeof hover !== 'undefined' && hover >= x) ||
												(typeof hover === 'undefined' &&
													typeof meta.value !== 'undefined' &&
													(meta.value || 1) >= x) ? (
													<Icon size="5" className="text-yellow-8">
														<RxStarFilled />
													</Icon>
												) : (
													<Icon size="5" color="gray">
														<RxStar />
													</Icon>
												)}
												<Label
													htmlFor={`${meta.id}-${x}`}
													color="gray"
													className="sr-only select-none"
												>
													Star {x}
												</Label>
											</RadioGroupPrimitive.Item>
										</Flex>
									)
								})}
							</RadioGroupPrimitive.Root>
						</Flex>
						{!!meta.errors && <FormFieldError error={meta.errors[0]} t={t} />}
					</Flex>
				</Box>
			</Flex>
		)
	},
)
RatingInput.displayName = 'RatingInput'

/* -----------------------------------------------------------------------------------------------*/

export { RatingInput }
